// components/Footer.tsx
"use client";
import { useState } from 'react';
import { Flex, Image, Text, Input, Button, useToast, Box, Show } from "@chakra-ui/react";
import DeepMode from "@public/DeepMode.svg";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import Link from "next/link";
import { FaDiscord, FaInstagram, FaMediumM, FaTiktok } from "react-icons/fa";
import { IoLogoYoutube } from "react-icons/io";
import { SiX } from "react-icons/si";

const Footer: React.FC = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [newsletterError, setNewsletterError] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const toast = useToast();

  const handleNewsletterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewsletterEmail(e.target.value);
    if (newsletterError) {
      setNewsletterError(false);
      setErrorMessage('');
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setNewsletterError(false);
    setNewsletterSuccess(false);

    try {
      const response = await fetch("https://api.deepmode.ai/users/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: newsletterEmail }),
      });

      if (!response.ok) throw new Error("Network response was not ok");
      const responseData = await response.json();
      setNewsletterSuccess(true);
      toast({ title: "Subscribed!", description: "Thanks for joining our newsletter!", status: "success" });
    } catch (error) {
      setNewsletterError(true);
      setErrorMessage("Failed to subscribe. Please check your email and try again.");
      console.error("There was a problem with the fetch operation:", error);
      toast({ title: "Error", description: "Failed to subscribe.", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const links = {
    services: [
      { link: "Generate AI Images", href: "/" },
      { link: "AI Anime Generator", href: "/anime" },
      { link: "AI Fashion Models", href: "/generate-ai-fashion-models" },
      { link: "AI Dating Site", href: "/ai-dating-photos" },
      { link: "AI Headshot Generators", href: "/ai-headshot-generator" },   
    ],
    about: [
      { link: "About Us", href: "/about-us" },
      { link: "Contact Us", href: "/contact-us" },
      { link: "Pricing", href: "/payments" },
      { link: "Blog", href: "https://deepmode.com/blog" },
      { link: "Newsletter", href: "https://deepmode.com/newsletter" },
    ],
    businessApi: [
      { link: "Become Affiliate", href: "https://deepmode.firstpromoter.com/" },
    ],
    legal: [
      { link: "Terms & Privacy", href: "/terms-and-conditions" },
    ],
  };
  

  const socials = [
    { social: "discord", link: "https://discord.gg/CMwtccKthy", icon: <FaDiscord size={24} color="black" /> },
    { social: "instagram", link: "https://www.instagram.com/deepmodeai/", icon: <FaInstagram size={24} color="black" /> },
    { social: "medium", link: "https://medium.com/@deepmodeai", icon: <FaMediumM size={24} color="black" /> },
    { social: "x", link: "https://x.com/deepmode_", icon: <SiX size={24} color="black" /> },
    { social: "youtube", link: "https://www.youtube.com/@Deepmode-ai", icon: <IoLogoYoutube size={24} color="black" /> },
    { social: "tiktok", link: "https://www.tiktok.com/@deepmode.ai", icon: <FaTiktok size={24} color="black" /> },
  ];

  return (
    <>
      {/* Desktop Version (>= 768px) */}
      <Show above="md">
        <Flex direction="column" bg="#050918" px={{ base: 0, md: "64px" }} py="58px">
          <Link href="/">
            <Image ml={{ base: "10px", md: "0px" }} w="128px" src={DeepMode.src} />
          </Link>
          <form onSubmit={handleNewsletterSubmit}>
            <Flex
              direction={{ base: "column", md: "row" }}
              borderRadius="12px"
              align={{ base: "start", md: "center" }}
              justify="space-between"
              px={{ base: "10px", md: "48px" }}
              py="32px"
              bg="#1F2434"
              mt="52px"
            >
              <Flex maxW={{ base: "", md: "900px" }} direction="column">
                <Text fontWeight="semibold" fontSize={{ base: "26px", md: "28px" }}>
                  Get access to Exclusive Deals & Latest Updates
                </Text>
                <Input
                  mt="25px"
                  bg="#1A1D21"
                  border="1px solid #363A3D"
                  borderColor={newsletterError ? 'red.300' : 'inherit'}
                  placeholder="Email"
                  onChange={handleNewsletterChange}
                  value={newsletterEmail}
                  isInvalid={newsletterError}
                  isDisabled={loading}
                />
                {newsletterError && <Text color="red.500" mt="2" fontSize="sm">{errorMessage}</Text>}
              </Flex>
              <Button color={{ base: "white", md: "black" }} bg={{ base: "#1F2434", md: "white" }} mt="15px" type="submit" rightIcon={<ArrowForwardIcon />} isLoading={loading}>
                Join the Newsletter
              </Button>
            </Flex>
          </form>
          <Flex direction="row" justify="space-between" mt="30px">
            <Flex direction="row" gap="60px">
              <Box>
                <Text fontWeight="bold" fontSize="16px" color="white">Services</Text>
                {links.services.map((link, index) => (
                  <Text mt="10px" color="#8F9FA3" fontSize="16px" key={index}>
                    <Link href={link.href}>
                      {link.link}
                    </Link>
                  </Text>
                ))}
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="16px" color="white">About</Text>
                {links.about.map((link, index) => (
                  <Text mt="10px" color="#8F9FA3" fontSize="16px" key={index}>
                    <Link href={link.href}>
                      {link.link}
                    </Link>
                  </Text>
                ))}
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="16px" color="white">Business</Text>
                {links.businessApi.map((link, index) => (
                  <Text mt="10px" color="#8F9FA3" fontSize="16px" key={index}>
                    <Link href={link.href}>
                      {link.link}
                    </Link>
                  </Text>
                ))}
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="16px" color="white">Legal</Text>
                {links.legal.map((link, index) => (
                  <Text mt="10px" color="#8F9FA3" fontSize="16px" key={index}>
                    <Link href={link.href}>
                      {link.link}
                    </Link>
                  </Text>
                ))}
              </Box>
            </Flex>
            <Flex direction="row" gap="10px" align="flex-end" mt="auto">
              {socials.map((social, index) => (
                <Flex
                  key={index}
                  w="40px"
                  h="40px"
                  bg="white"
                  justify="center"
                  align="center"
                  borderRadius="full"
                >
                  <Link href={social.link}>
                    {social.icon}
                  </Link>
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Flex>
        <Text textAlign="center" color="gray" fontSize="12px">
        Operated by Nefertiti Limited

        </Text>
        <Text textAlign="center" color="gray" fontSize="12px">
        Room B, 7/F, One Capital Place
18 Luard Road
Wan Chai
Hong Kong        </Text>
      </Show>

      {/* Mobile Version (< 768px) */}
      <Show below="md">
        <Flex direction="column" bg="#050918" px="20px" py="58px">
          <Link href="/">
            <Image ml="10px" w="128px" src={DeepMode.src} />
          </Link>
          <form onSubmit={handleNewsletterSubmit}>
            <Flex
              direction="column"
              borderRadius="12px"
              align="start"
              justify="space-between"
              px="10px"
              py="32px"
              bg="#1F2434"
              mt="52px"
            >
              <Flex direction="column">
                <Text fontWeight="semibold" fontSize="26px">
                  Get access to Exclusive Deals & Latest Updates
                </Text>
                <Input
                  mt="25px"
                  bg="#1A1D21"
                  border="1px solid #363A3D"
                  borderColor={newsletterError ? 'red.300' : 'inherit'}
                  placeholder="Email"
                  onChange={handleNewsletterChange}
                  value={newsletterEmail}
                  isInvalid={newsletterError}
                  isDisabled={loading}
                />
                {newsletterError && <Text color="red.500" mt="2" fontSize="sm">{errorMessage}</Text>}
              </Flex>
              <Button color="white" bg="#1F2434" mt="15px" type="submit" rightIcon={<ArrowForwardIcon />} isLoading={loading}>
                Join the Newsletter
              </Button>
            </Flex>
          </form>
          <Flex direction="column" mt="30px" align="start">
              <Box>
                <Text fontWeight="bold" fontSize="16px" color="white">Services</Text>
                {links.services.map((link, index) => (
                  <Box key={index}>
                    <Text mt="10px" color="#8F9FA3" fontSize="16px">
                      <Link href={link.href}>
                        {link.link}
                      </Link>
                    </Text>
                  </Box>
                ))}
              </Box>
              <Box mt="20px">
                <Text fontWeight="bold" fontSize="16px" color="white">About</Text>
                {links.about.map((link, index) => (
                  <Box key={index}>
                    <Text mt="10px" color="#8F9FA3" fontSize="16px">
                      <Link href={link.href}>
                        {link.link}
                      </Link>
                    </Text>
                  </Box>
                ))}
              </Box>
              <Box mt="20px">
                <Text fontWeight="bold" fontSize="16px" color="white">Business</Text>
                {links.businessApi.map((link, index) => (
                  <Box key={index}>
                    <Text mt="10px" color="#8F9FA3" fontSize="16px">
                      <Link href={link.href}>
                        {link.link}
                      </Link>
                    </Text>
                  </Box>
                ))}
              </Box>
              <Box mt="20px">
                <Text fontWeight="bold" fontSize="16px" color="white">Legal</Text>
                {links.legal.map((link, index) => (
                  <Box key={index}>
                    <Text mt="10px" color="#8F9FA3" fontSize="16px">
                      <Link href={link.href}>
                        {link.link}
                      </Link>
                    </Text>
                  </Box>
                ))}
              </Box>
            </Flex>

          <Flex mt="50px" justify="center" gap="10px">
            {socials.map((social, index) => (
              <Flex
                key={index}
                w="40px"
                h="40px"
                bg="white"
                justify="center"
                align="center"
                borderRadius="full"
              >
                <Link href={social.link}>
                  {social.icon}
                </Link>
              </Flex>
            ))}
          </Flex>
        </Flex>
        <Text textAlign="center" color="gray" fontSize="12px">
        Operated by DEEPMODE LTD

        </Text>
        <Text textAlign="center" color="gray" fontSize="12px">
        Spyrou Kyprianou & Agias Phylaxeos (Corner), 182 KOFTEROS BUSINESS CENTER, Office 201, 3083, Limassol, Cyprus
        </Text>
      </Show>
    </>
  );
};

export default Footer;
